import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../components/RequestRouting";
import {
  filterReleaseNotes,
  parseReleaseNotes,
  releaseKindTone,
  RELEASE_NOTES_MAX,
  type ReleaseNoteEntry,
} from "../lib/releaseNotes";

export function ReleaseNotesPage() {
  const [entries, setEntries] = useState<ReleaseNoteEntry[]>([]);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/CHANGELOG.md")
      .then((r) => {
        if (!r.ok) throw new Error("Failed to load");
        return r.text();
      })
      .then((md) => setEntries(parseReleaseNotes(md, RELEASE_NOTES_MAX)))
      .catch(() => setError("Could not load release notes."));
  }, []);

  const visible = useMemo(() => filterReleaseNotes(entries, query), [entries, query]);

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title="Release notes"
        subtitle="What shipped in the demo — searchable, newest first (latest 20). Each release is Foundational, New feature, Enhancement, or Bug fix."
      />

      <label className="block">
        <span className="sr-only">Search release notes</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search releases or type (e.g. foundational)…"
          className="w-full border border-black/10 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal/40"
        />
      </label>

      {error && <p className="text-sm text-red-700">{error}</p>}

      {!error && entries.length === 0 && (
        <p className="text-sm text-slate">Loading release notes…</p>
      )}

      {!error && entries.length > 0 && visible.length === 0 && (
        <p className="text-sm text-slate">No releases match “{query}”.</p>
      )}

      <ul className="space-y-4">
        {visible.map((e) => (
          <li key={e.id} className="bg-white border border-black/5">
            <div className="px-5 py-4 border-b border-black/5 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  {e.kind && (
                    <span
                      className={`inline-block px-1.5 py-0.5 text-xs font-medium ${releaseKindTone(e.kind)}`}
                    >
                      {e.kind}
                    </span>
                  )}
                  {e.date && (
                    <time className="text-xs font-medium tracking-wide uppercase text-slate">
                      {e.date}
                    </time>
                  )}
                </div>
                <h2 className="font-display text-lg text-navy">{e.title}</h2>
              </div>
            </div>
            {e.bullets.length > 0 ? (
              <ul className="px-5 py-4 space-y-2">
                {e.bullets.map((b, i) => (
                  <li key={i} className="text-sm text-ink/90 flex gap-2">
                    <span className="text-teal mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-teal" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-5 py-4 text-sm text-slate">{e.heading}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
